/* ========================================================================
   Machine Compare App - JavaScript
   ======================================================================== */
(function() {
  'use strict';

  // ---- Data (will be replaced with inline JSON for production) ----
  let MACHINES = [];

  // ---- State ----
  const state = {
    machines: [],
    filtered: [],
    filters: {
      types: [],
      brands: [],
      priceMin: 0,
      priceMax: 35000,
      onSale: false,
      search: '',
      promo: false,
      preowned: false
    },
    sort: 'price-asc',
    compare: [],       // Array of handles (max 3)
    view: 'browse',    // 'browse' or 'compare'
    page: 1,
    perPage: 24
  };

  // ---- Shared localStorage sync ----
  var COMPARE_STORAGE_KEY = 'qsv-compare-machines';
  var _mcaBroadcasting = false; // prevents event loops

  // Register qsv-compare-now early (before data fetch) so clicking "Compare Now"
  // in the floating tray works even if the user clicks before data has loaded.
  var _pendingCompareNow = false;
  window.addEventListener('qsv-compare-now', function() {
    if (state.machines.length === 0) { _pendingCompareNow = true; }
    else { showCompareView(); }
  });

  function syncCompareToStorage() {
    try {
      var items = state.compare.map(function(h) {
        var m = state.machines.find(function(x) { return x.handle === h; });
        if (!m) return null;
        return {
          handle: m.handle,
          title: m.title,
          image: m.image || '',
          url: m.url || '',
          price: m.isQuoteRequest ? 'Call for Price' : (m.price ? '$' + m.price.toLocaleString() : '')
        };
      }).filter(Boolean);
      localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(items));
    } catch(e) {}

    _mcaBroadcasting = true;
    window.dispatchEvent(new CustomEvent('qsv-compare-updated', {
      detail: { handles: state.compare.slice() }
    }));
    _mcaBroadcasting = false;
  }

  function loadCompareFromStorage() {
    try {
      var data = localStorage.getItem(COMPARE_STORAGE_KEY);
      if (!data) return [];
      var parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        return parsed.map(function(item) { return item.handle; }).filter(Boolean).slice(0, 3);
      }
    } catch(e) {}
    return [];
  }

  // ---- Brand collection URLs ----
  var BRAND_URLS = {
    'Baby Lock': '/collections/baby-lock-machines',
    'Bernette': '/collections/bernette',
    'Bernina': '/collections/bernina',
    'Brother': '/collections/brother-sewing-machine-and-accessories',
    'Grace': '/collections/grace',
    'Handi Quilter': '/collections/handi-quilter',
    'Janome': '/collections/janome',
    'Juki': '/collections/juki',
    'Pfaff': '/collections/pfaff-sewing-machines',
    'Singer': '/collections/singer-sewing-machines',
    'Viking Husqvarna': '/collections/husqvarna-viking'
  };

  function brandLink(brand) {
    var url = BRAND_URLS[brand];
    if (url) return '<a href="' + url + '" class="mca-cph-brand-link" target="_blank" rel="noopener">' + brand + '</a>';
    return brand;
  }

  // ---- DOM refs ----
  const $ = (sel) => document.querySelector(sel);
  const $$ = (sel) => document.querySelectorAll(sel);

  const dom = {
    grid: $('#mca-card-grid'),
    resultCount: $('#mca-result-count'),
    activeFilters: $('#mca-active-filters'),
    loadMoreWrap: $('#mca-load-more-wrap'),
    loadMore: $('#mca-load-more'),
    noResults: $('#mca-no-results'),
    searchInput: $('#mca-search-input'),
    sortSelect: $('#mca-sort-select'),
    sortSpecs: $('#mca-sort-specs'),
    typeFilters: $('#mca-type-filters'),
    brandFilters: $('#mca-brand-filters'),
    brandToggle: $('#mca-brand-toggle'),
    priceMin: $('#mca-price-min'),
    priceMax: $('#mca-price-max'),
    priceMinLabel: $('#mca-price-min-label'),
    priceMaxLabel: $('#mca-price-max-label'),
    rangeFill: $('#mca-range-fill'),
    saleToggle: $('#mca-sale-toggle'),
    compareTray: $('#mca-compare-tray'),
    trayItems: $('#mca-tray-items'),
    trayCount: $('#mca-tray-count'),
    browseView: $('#mca-browse-view'),
    compareView: $('#mca-compare-view'),
    compareTable: $('#mca-compare-table'),
    sidebar: $('#mca-sidebar'),
    overlay: $('#mca-overlay')
  };

  // ---- Constants ----
  const TYPE_ORDER = ['Sewing', 'Embroidery', 'Quilting', 'Serger', 'Longarm', 'Coverstitch', 'Multi-Needle', 'Cutting', 'Specialty'];
  const BRAND_MIN_COUNT = 5;
  const SPEC_CATEGORY_ORDER = [
    'Key Specs', 'Sewing Features', 'Quilting Performance', 'Embroidery Features',
    'Threading & Bobbin', 'Workspace & Display', 'Smart Features',
    'Included Feet', 'Presser Feet', 'Physical Specifications', 
    'Included Hoops', 'Included Accessories', 'Warranty'
  ];

  // ---- Key Specs row definitions ----
  var KEY_SPEC_ROWS_SEWING = [
    { key: 'stitchCount',      label: 'Built-in Stitches' },
    { key: 'buttonholes',     label: 'Buttonholes' },
    { key: 'maxSpeed',        label: 'Max Speed' },
    { key: 'needleThreader',  label: 'Needle Threader' },
    { key: 'threadCutter',    label: 'Thread Cutter' },
    { key: 'display',         label: 'Display' },
    { key: 'throatSpace',     label: 'Throat Space' },
    { key: 'includedFeet',    label: 'Included Feet' },
    { key: 'warranty',        label: 'Warranty' }
  ];

  var KEY_SPEC_ROWS_EMBROIDERY = [
    { key: 'embroideryDesigns', label: 'Embroidery Designs' },
    { key: 'embroideryArea',   label: 'Max Embroidery Area' },
    { key: 'maxSpeed',        label: 'Max Speed' },
    { key: 'needleThreader',  label: 'Needle Threader' },
    { key: 'threadCutter',    label: 'Thread Cutter' },
    { key: 'display',         label: 'Display' },
    { key: 'throatSpace',     label: 'Throat Space' },
    { key: 'includedFeet',    label: 'Included Feet' },
    { key: 'warranty',        label: 'Warranty' }
  ];

  var KEY_SPEC_ROWS_COMBO = [
    { key: 'stitchCount',      label: 'Built-in Stitches' },
    { key: 'embroideryDesigns', label: 'Embroidery Designs' },
    { key: 'embroideryArea',   label: 'Max Embroidery Area' },
    { key: 'buttonholes',     label: 'Buttonholes' },
    { key: 'maxSpeed',        label: 'Max Speed' },
    { key: 'needleThreader',  label: 'Needle Threader' },
    { key: 'threadCutter',    label: 'Thread Cutter' },
    { key: 'display',         label: 'Display' },
    { key: 'throatSpace',     label: 'Throat Space' },
    { key: 'includedFeet',    label: 'Included Feet' },
    { key: 'warranty',        label: 'Warranty' }
  ];

  var KEY_SPEC_ROWS_SERGER = [
    { key: 'threadCount',     label: 'Thread Count' },
    { key: 'stitchCount',     label: 'Built-in Stitches' },
    { key: 'maxSpeed',        label: 'Max Speed' },
    { key: 'threadingSystem', label: 'Threading System' },
    { key: 'throatSpace',     label: 'Throat Space' },
    { key: 'includedFeet',    label: 'Included Feet' },
    { key: 'warranty',        label: 'Warranty' }
  ];

  var KEY_SPEC_ROWS_LONGARM = [
    { key: 'maxSpeed',        label: 'Max Speed' },
    { key: 'throatSpace',     label: 'Throat Space' },
    { key: 'display',         label: 'Display' },
    { key: 'includedFeet',    label: 'Included Feet' },
    { key: 'warranty',        label: 'Warranty' }
  ];

  function getKeySpecRows(machines) {
    var hasSerger = false, hasCoverstitch = false;
    var hasSewing = false, hasEmbroidery = false, hasLongarm = false;
    machines.forEach(function(m) {
      var t = (m.types || []);
      t.forEach(function(type) {
        if (type === 'Serger') hasSerger = true;
        if (type === 'Coverstitch') hasCoverstitch = true;
        if (type === 'Sewing') hasSewing = true;
        if (type === 'Embroidery') hasEmbroidery = true;
        if (type === 'Longarm') hasLongarm = true;
      });
    });
    if ((hasSerger || hasCoverstitch) && !hasSewing && !hasEmbroidery && !hasLongarm) return KEY_SPEC_ROWS_SERGER;
    if (hasLongarm && !hasSewing && !hasEmbroidery && !hasSerger && !hasCoverstitch) return KEY_SPEC_ROWS_LONGARM;
    if (hasEmbroidery && !hasSewing && !hasSerger && !hasCoverstitch) return KEY_SPEC_ROWS_EMBROIDERY;
    if (hasEmbroidery && hasSewing) return KEY_SPEC_ROWS_COMBO;
    if (hasSerger || hasCoverstitch) {
      var merged = KEY_SPEC_ROWS_COMBO.slice();
      var existingKeys = {};
      merged.forEach(function(r) { existingKeys[r.key] = true; });
      KEY_SPEC_ROWS_SERGER.forEach(function(r) { if (!existingKeys[r.key]) merged.push(r); });
      return merged;
    }
    return KEY_SPEC_ROWS_SEWING;
  }

  function extractKeySpecs(machine) {
    var result = {};
    var items = (machine.specs && machine.specs['Key Specs']) || [];
    var cs = machine.cardSpecs || {};

    // Thread Count
    if (cs.threadCount) { result.threadCount = cs.threadCount; }
    else {
      for (var i = 0; i < items.length; i++) {
        var m = items[i].match(/^(\d(?:\/\d)+)\s+Thread/i);
        if (m) { result.threadCount = m[1] + ' thread'; break; }
      }
    }

    // Threading System
    for (var i = 0; i < items.length; i++) {
      var s = items[i];
      if (/RevolutionAir/i.test(s)) { result.threadingSystem = 'RevolutionAir Needle & Looper Air Threading'; break; }
      if (/ExtraordinAir/i.test(s)) { result.threadingSystem = 'ExtraordinAir Looper Air Threading'; break; }
      if (/Jet-Air Threading/i.test(s)) { result.threadingSystem = 'Jet-Air Looper Threading'; break; }
      if (/Air Threading|Air Threader|One-Touch Air|One-Push Air|Blast Air/i.test(s)) { result.threadingSystem = 'Air Looper Threading'; break; }
      if (/Color-Coded Threading/i.test(s)) { result.threadingSystem = 'Color-Coded Threading'; break; }
      if (/Lay-In Threading/i.test(s)) { result.threadingSystem = 'Lay-In Threading'; break; }
    }

    // Stitch Count
    if (cs.stitchCount) { result.stitchCount = cs.stitchCount.toLocaleString(); }
    else {
      for (var i = 0; i < items.length; i++) {
        var m = items[i].match(/^(?:Over\s+)?(\d[\d,]*)\s+(?:Built-in\s+)?Stitch/i);
        if (m) { result.stitchCount = m[0].replace(/\s+Built-in\s+Stitches?$/i, '').replace(/\s+Stitch(?:es|Patterns)?.*$/i, '') + ' Stitches'; break; }
      }
    }

    // Embroidery Designs
    for (var i = 0; i < items.length; i++) {
      var m = items[i].match(/(\d[\d,]*\+?)\s+(?:Built-in\s+)?[Ee]mbroidery [Dd]esigns/i);
      if (m) { result.embroideryDesigns = m[1]; break; }
    }

    // Embroidery Area
    if (cs.embroideryArea) { result.embroideryArea = cs.embroideryArea; }
    else {
      for (var i = 0; i < items.length; i++) {
        var s = items[i];
        if (/Embroidery/i.test(s) && /[\d.\-\/]+\s*["\u201d]?\s*x\s*[\d.\-\/]+\s*["\u201d]?/i.test(s)) {
          var m = s.match(/([\d.\-\/]+)\s*["\u201d]?\s*x\s*([\d.\-\/]+)\s*["\u201d]?/i);
          if (m) { result.embroideryArea = m[1] + '" x ' + m[2] + '"'; break; }
          m = s.match(/(\d+)\s*mm\s*x\s*(\d+)\s*mm/i);
          if (m) { result.embroideryArea = m[1] + 'mm x ' + m[2] + 'mm'; break; }
        }
      }
    }

    // Buttonholes
    for (var i = 0; i < items.length; i++) {
      var m = items[i].match(/^(\d+)\s+(One-Step\s+|Sensor\s+|Automatic\s+)?Buttonhole/i);
      if (m) { result.buttonholes = m[0].replace(/s?$/i, 's'); break; }
    }

    // Max Speed
    if (cs.maxSpeed) { result.maxSpeed = cs.maxSpeed.toLocaleString() + ' SPM'; }
    else {
      for (var i = 0; i < items.length; i++) {
        var m = items[i].match(/(\d[\d,]*)\s+(?:Stitches?\s+Per\s+Minute|SPM)/i);
        if (m) { result.maxSpeed = m[1] + ' SPM'; break; }
      }
    }

    // Needle Threader
    for (var i = 0; i < items.length; i++) {
      var s = items[i];
      if (/needle threader|RevolutionAir|Air Threader|Manual Threading/i.test(s)) {
        var m = s.match(/Needle Threader:\s*(.+)/i);
        if (m) { result.needleThreader = m[1].trim(); }
        else if (/Built-in Needle Threader/i.test(s)) { result.needleThreader = 'Built-in'; }
        else if (/Automatic/i.test(s)) { result.needleThreader = 'Automatic'; }
        else if (/Manual/i.test(s)) { result.needleThreader = 'Manual'; }
        else { result.needleThreader = s.trim(); }
        break;
      }
    }

    // Thread Cutter
    for (var i = 0; i < items.length; i++) {
      if (/Thread Cutter/i.test(items[i])) {
        if (/Automatic/i.test(items[i])) { result.threadCutter = 'Automatic'; }
        else if (/Manual/i.test(items[i])) { result.threadCutter = 'Manual'; }
        else { result.threadCutter = items[i].replace(/^Thread Cutter:\s*/i, '').trim(); }
        break;
      }
    }

    // Display
    if (cs.screenSize) { result.display = cs.screenSize + '" Touchscreen'; }
    else {
      for (var i = 0; i < items.length; i++) {
        var s = items[i];
        if (/Touchscreen|LCD|Screen|Display|Dial/i.test(s) && !/Thread/i.test(s)) {
          result.display = s.replace(/^Display:\s*/i, '').trim();
          break;
        }
      }
    }

    // Throat Space
    if (cs.throatSpace) { result.throatSpace = cs.throatSpace; }
    else {
      for (var i = 0; i < items.length; i++) {
        var m = items[i].match(/^([\d.\-\/]+)["\u201d]\s+(?:Throat|to the Right|Sewing Space|throat)/i);
        if (m) { result.throatSpace = m[1] + '"'; break; }
      }
    }

    // Included Feet
    if (cs.includedFeet) { result.includedFeet = cs.includedFeet; }
    else {
      for (var i = 0; i < items.length; i++) {
        var m = items[i].match(/(?:Includes?\s+)?(\d+)\s+(?:Presser\s+)?Fee?t/i);
        if (m) { result.includedFeet = parseInt(m[1]); break; }
      }
    }

    // Warranty
    for (var i = 0; i < items.length; i++) {
      if (/warranty/i.test(items[i])) {
        result.warranty = items[i].replace(/^Limited Warranty:\s*/i, '');
        break;
      }
    }
    return result;
  }

  // ---- Helpers ----
  function formatPrice(p) {
    if (p == null) return '';
    return '$' + p.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function calcSavings(compare, price) {
    if (!compare || !price || compare <= price) return null;
    return Math.round((1 - price / compare) * 100);
  }

  function debounce(fn, ms) {
    let timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function stripBrand(title, brand) {
    const re = new RegExp('^' + brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i');
    return title.replace(re, '');
  }

  // ---- Initialize ----
  function init(data) {
    MACHINES = data;
    state.machines = data;
    const prices = data.filter(m => m.price && !m.isQuoteRequest).map(m => m.price);
    const actualMin = prices.length ? Math.floor(Math.min(...prices) / 100) * 100 : 0;
    const actualMax = prices.length ? Math.ceil(Math.max(...prices) / 100) * 100 : 50000;
    dom.priceMin.min = actualMin;
    dom.priceMin.max = actualMax;
    dom.priceMax.min = actualMin;
    dom.priceMax.max = actualMax;
    dom.priceMin.value = actualMin;
    dom.priceMax.value = actualMax;
    state.filters.priceMin = actualMin;
    state.filters.priceMax = actualMax;

    buildTypeFilters();
    buildBrandFilters();
    updateSpecialCounts();
    bindEvents();

    state.filters.types = ['Sewing'];
    const sewingCheckbox = dom.typeFilters.querySelector('input[value="Sewing"]');
    if (sewingCheckbox) sewingCheckbox.checked = true;

    applyFilters();

    if (_pendingCompareNow) { _pendingCompareNow = false; showCompareView(); }
  }

  function buildTypeFilters() {
    const counts = {};
    TYPE_ORDER.forEach(t => counts[t] = 0);
    state.machines.forEach(m => m.types.forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
    dom.typeFilters.innerHTML = TYPE_ORDER.filter(t => counts[t] > 0).map(t => `
        <li><label><input type="checkbox" value="${t}"> ${t} <span class="mca-filter-count">(${counts[t]})</span></label></li>
      `).join('');
  }

  function buildBrandFilters() {
    const counts = {};
    state.machines.forEach(m => { counts[m.brand] = (counts[m.brand] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const major = sorted.filter(([, c]) => c >= BRAND_MIN_COUNT);
    const minor = sorted.filter(([, c]) => c < BRAND_MIN_COUNT);
    let html = major.map(([b, c]) => `<li><label><input type="checkbox" value="${b}"> ${b} <span class="mca-filter-count">(${c})</span></label></li>`).join('');
    if (minor.length) {
      html += minor.map(([b, c]) => `<li class="mca-brand-extra" style="display:none"><label><input type="checkbox" value="${b}"> ${b} <span class="mca-filter-count">(${c})</span></label></li>`).join('');
      dom.brandToggle.style.display = '';
      dom.brandToggle.textContent = `Show ${minor.length} more`;
    }
    dom.brandFilters.innerHTML = html;
  }

  function updateSpecialCounts() {
    $('#mca-promo-count').textContent = `(${state.machines.filter(m => m.isPromo).length})`;
    $('#mca-preowned-count').textContent = `(${state.machines.filter(m => m.isPreOwned).length})`;
  }

  function applyFilters() {
    const f = state.filters;
    let results = state.machines.filter(m => {
      if (f.types.length && !m.types.some(t => f.types.includes(t))) return false;
      if (f.brands.length && !f.brands.includes(m.brand)) return false;
      const priceFilterActive = f.priceMin > parseInt(dom.priceMin.min) || f.priceMax < parseInt(dom.priceMax.max);
      if (m.isQuoteRequest && priceFilterActive) return false;
      if (!m.isQuoteRequest && m.price != null) {
        if (m.price < f.priceMin || m.price > f.priceMax) return false;
      }
      if (f.onSale && !m.isOnSale) return false;
      if (f.promo && !m.isPromo) return false;
      if (f.preowned && !m.isPreOwned) return false;
      if (f.search) {
        const q = f.search.toLowerCase();
        const searchable = (m.title + ' ' + m.brand + ' ' + (m.tagline || '') + ' ' + m.types.join(' ')).toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    });
    results = sortMachines(results, state.sort);
    state.filtered = results;
    state.page = 1;
    renderCards();
    updateCardButtons();
    renderActiveFilters();
    updateResultCount();
  }

  function parseSpecNumber(val) {
    if (!val) return 0;
    var match = String(val).match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : 0;
  }

  function parseEmbroideryArea(val) {
    if (!val) return 0;
    var s = String(val);
    var nums = s.match(/[\d.]+/g);
    if (!nums || !nums.length) return 0;
    var values = nums.map(Number);
    var isMM = s.toLowerCase().indexOf('mm') !== -1;
    var maxVal = Math.max.apply(null, values);
    return isMM ? maxVal / 25.4 : maxVal;
  }

  function sortMachines(arr, sortKey) {
    const sorted = [...arr];
    switch (sortKey) {
      case 'price-asc': sorted.sort((a, b) => (a.isQuoteRequest ? Infinity : (a.price || Infinity)) - (b.isQuoteRequest ? Infinity : (b.price || Infinity))); break;
      case 'price-desc': sorted.sort((a, b) => (b.isQuoteRequest ? -1 : (b.price || 0)) - (a.isQuoteRequest ? -1 : (a.price || 0))); break;
      case 'name-asc': sorted.sort((a, b) => a.title.localeCompare(b.title)); break;
      case 'name-desc': sorted.sort((a, b) => b.title.localeCompare(a.title)); break;
      case 'brand-asc': sorted.sort((a, b) => a.brand.localeCompare(b.brand) || a.title.localeCompare(b.title)); break;
      case 'stitches-desc': sorted.sort((a, b) => (b.cardSpecs.stitchCount || 0) - (a.cardSpecs.stitchCount || 0)); break;
      case 'speed-desc': sorted.sort((a, b) => (b.cardSpecs.maxSpeed || 0) - (a.cardSpecs.maxSpeed || 0)); break;
      case 'throat-desc': sorted.sort((a, b) => parseSpecNumber(b.cardSpecs.throatSpace) - parseSpecNumber(a.cardSpecs.throatSpace)); break;
      case 'embroidery-desc': sorted.sort((a, b) => parseEmbroideryArea(b.cardSpecs.embroideryArea) - parseEmbroideryArea(a.cardSpecs.embroideryArea)); break;
    }
    return sorted;
  }

  function renderCards() {
    const visible = state.filtered.slice(0, state.page * state.perPage);
    if (state.filtered.length === 0) {
      dom.grid.innerHTML = ''; dom.noResults.style.display = ''; dom.loadMoreWrap.style.display = 'none'; return;
    }
    dom.noResults.style.display = 'none';
    dom.grid.innerHTML = visible.map(m => {
      const isSelected = state.compare.includes(m.handle);
      let specsHtml = '';
      const cs = m.cardSpecs;
      if (cs.stitchCount) specsHtml += `<div class="mca-card-spec"><span class="mca-card-spec-label">Stitches:</span> <span class="mca-card-spec-value">${cs.stitchCount.toLocaleString()}</span></div>`;
      if (cs.maxSpeed) specsHtml += `<div class="mca-card-spec"><span class="mca-card-spec-label">Speed:</span> <span class="mca-card-spec-value">${cs.maxSpeed.toLocaleString()} spm</span></div>`;
      if (cs.embroideryArea) specsHtml += `<div class="mca-card-spec"><span class="mca-card-spec-label">Emb Area:</span> <span class="mca-card-spec-value">${cs.embroideryArea}</span></div>`;
      if (cs.screenSize) specsHtml += `<div class="mca-card-spec"><span class="mca-card-spec-label">Screen:</span> <span class="mca-card-spec-value">${cs.screenSize}</span></div>`;
      if (cs.includedFeet) specsHtml += `<div class="mca-card-spec"><span class="mca-card-spec-label">Feet:</span> <span class="mca-card-spec-value">${cs.includedFeet}</span></div>`;
      if (cs.throatSpace) specsHtml += `<div class="mca-card-spec"><span class="mca-card-spec-label">Throat:</span> <span class="mca-card-spec-value">${cs.throatSpace}</span></div>`;
      if (cs.threadCount) specsHtml += `<div class="mca-card-spec"><span class="mca-card-spec-label">Threads:</span> <span class="mca-card-spec-value">${cs.threadCount}</span></div>`;
      let badgesHtml = '';
      if (m.isPromo) badgesHtml += `<span class="mca-badge mca-badge-promo">Promo</span>`;
      if (m.isPreOwned) badgesHtml += `<span class="mca-badge mca-badge-preowned">${m.title.toLowerCase().includes('open box') ? 'Open Box' : 'Pre-Owned'}</span>`;
      return `
        <article class="mca-card${isSelected ? ' mca-card-selected' : ''}" data-handle="${m.handle}">
          <div class="mca-card-image">${badgesHtml ? `<div class="mca-card-badges">${badgesHtml}</div>` : ''}
            ${m.image ? `<img src="${m.image}" alt="${m.title}" loading="lazy">` : `<span class="mca-no-image">No image</span>`}
            <div class="mca-card-brand-types"><span class="mca-brand-badge">${m.brand}</span>${m.types.map(t => `<span class="mca-type-badge">${t}</span>`).join('')}</div>
          </div>
          <div class="mca-card-body">
            <div class="mca-card-title-row"><h3 class="mca-card-title">${stripBrand(m.title, m.brand)}</h3>
              <span class="mca-card-price">${m.isQuoteRequest ? 'Call' : formatPrice(m.price)}</span>
            </div>
            ${specsHtml ? `<div class="mca-card-specs">${specsHtml}</div>` : ''}
            <div class="mca-card-actions">
              <a href="${m.url}" class="mca-btn-view" target="_blank" rel="noopener">View</a>
              <button class="mca-btn-compare${isSelected ? ' active' : ''}" data-handle="${m.handle}">${isSelected ? '&#10003; Added' : '+ Compare'}</button>
            </div>
          </div>
        </article>`;
    }).join('');
    dom.loadMoreWrap.style.display = visible.length < state.filtered.length ? '' : 'none';
    if (visible.length < state.filtered.length) dom.loadMore.textContent = `Load More (${state.filtered.length - visible.length} remaining)`;
  }

  function updateResultCount() { dom.resultCount.textContent = `${state.filtered.length} machine${state.filtered.length !== 1 ? 's' : ''}`; }

  function renderActiveFilters() {
    const f = state.filters; let chips = [];
    f.types.forEach(t => chips.push({ label: t, type: 'type', value: t }));
    f.brands.forEach(b => chips.push({ label: b, type: 'brand', value: b }));
    if (f.onSale) chips.push({ label: 'On Sale', type: 'sale' });
    if (f.promo) chips.push({ label: 'Promo', type: 'promo' });
    if (f.preowned) chips.push({ label: 'Pre-Owned', type: 'preowned' });
    if (f.search) chips.push({ label: `"${f.search}"`, type: 'search' });
    if (f.priceMin > parseInt(dom.priceMin.min) || f.priceMax < parseInt(dom.priceMax.max)) chips.push({ label: `${formatPrice(f.priceMin)} - ${formatPrice(f.priceMax)}`, type: 'price' });
    dom.activeFilters.innerHTML = chips.map(c => `<span class="mca-chip" data-type="${c.type}" data-value="${c.value || ''}">${c.label}<span class="mca-chip-remove">&times;</span></span>`).join('');
  }

  function updateCompareTray() {
    var ft = document.getElementById('mca-floating-tray');
    if (!ft) return;
    var count = state.compare.length;
    if (count === 0) { ft.classList.remove('visible'); return; }

    var countEl = ft.querySelector('.mca-ft-count');
    if (countEl) countEl.textContent = count + ' of 3';

    var itemsEl = ft.querySelector('.mca-ft-items');
    if (itemsEl) {
      itemsEl.innerHTML = state.compare.map(function(h) {
        var m = state.machines.find(function(x) { return x.handle === h; });
        if (!m) return '';
        var esc = document.createElement('div');
        esc.textContent = m.title || '';
        return '<div class="mca-ft-item">' +
          '<img class="mca-ft-item-img" src="' + (m.image || '') + '" alt="" loading="lazy">' +
          '<span class="mca-ft-item-name">' + esc.innerHTML + '</span>' +
          '<button class="mca-ft-item-remove" data-handle="' + h + '" aria-label="Remove">&times;</button>' +
          '</div>';
      }).filter(Boolean).join('');

      itemsEl.querySelectorAll('.mca-ft-item-remove').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          e.preventDefault(); e.stopPropagation();
          toggleCompare(btn.getAttribute('data-handle'));
        });
      });
    }

    if (!ft.classList.contains('visible')) {
      requestAnimationFrame(function() {
        requestAnimationFrame(function() { ft.classList.add('visible'); });
      });
    }
  }

  function initFloatingTray() {
    var ft = document.getElementById('mca-floating-tray');
    if (!ft) return;
    ft.querySelector('.mca-ft-collapse').addEventListener('click', function(e) {
      e.preventDefault(); ft.classList.toggle('collapsed');
    });
    ft.querySelector('.mca-ft-clear').addEventListener('click', function(e) {
      e.preventDefault(); clearAllCompare();
    });
    ft.querySelector('.mca-ft-compare-now').addEventListener('click', function(e) {
      e.preventDefault(); showCompareView();
    });
  }

  function toggleCompare(handle) {
    const idx = state.compare.indexOf(handle);
    if (idx > -1) { state.compare.splice(idx, 1); }
    else if (state.compare.length < 3) { state.compare.push(handle); }
    else {
      var clickedBtn = dom.grid.querySelector('.mca-btn-compare[data-handle="' + handle + '"]');
      if (clickedBtn) {
        clickedBtn.textContent = '3/3 Full'; clickedBtn.classList.add('mca-btn-max');
        setTimeout(() => { clickedBtn.textContent = '+ Compare'; clickedBtn.classList.remove('mca-btn-max'); }, 2000);
      }
      var floatingMsg = document.querySelector('#mca-floating-tray .mca-ft-max-msg');
      if (floatingMsg) { floatingMsg.classList.add('show'); setTimeout(() => floatingMsg.classList.remove('show'), 3000); }
      return;
    }
    updateCompareTray(); updateCardButtons(); syncCompareToStorage();
  }

  function clearAllCompare() { state.compare = []; updateCompareTray(); updateCardButtons(); syncCompareToStorage(); }

  function updateCardButtons() {
    $$('.mca-btn-compare').forEach(btn => {
      const sel = state.compare.includes(btn.dataset.handle);
      btn.classList.toggle('active', sel); btn.innerHTML = sel ? '&#10003; Added' : '+ Compare';
      btn.closest('.mca-card').classList.toggle('mca-card-selected', sel);
    });
  }

  let _mcaCompareTimer = null;
  let _mcaHasInteracted = false;

  function trackKlaviyoCompare(machines) {
    if (typeof klaviyo !== 'undefined' && machines.length > 0) {
      const titles = machines.map(m => m.title);
      const brands = [...new Set(machines.map(m => m.brand))];
      klaviyo.push(['track', 'High Intent Machine Comparison', {
        'MachineTitles': titles,
        'MachineBrands': brands,
        'MachineCount': machines.length,
        'CompareURL': window.location.href + '?compare=' + machines.map(m => m.handle).join(','),
        'IntentLevel': 'High'
      }]);
    }
  }

  function showCompareView() {
    if (state.compare.length === 0) {
      var stored = loadCompareFromStorage();
      if (stored.length) state.compare = stored.filter(h => state.machines.some(m => m.handle === h)).slice(0, 3);
    }
    if (state.compare.length === 0) return;
    try {
      state.view = 'compare'; renderCompareTable();
      dom.browseView.style.display = 'none'; dom.compareView.classList.add('active');
      dom.compareView.scrollIntoView({ behavior: 'smooth', block: 'start' });

      /* Update URL with compare handles */
      var compareUrl = window.location.pathname + '?compare=' + state.compare.join(',');
      history.replaceState(null, '', compareUrl);

      /* DataLayer: compare machines */
      var dlMachines = state.compare.map(function(h) { return state.machines.find(function(m) { return m.handle === h; }); }).filter(Boolean);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'compare_machines',
        compare_count: dlMachines.length,
        compare_handles: state.compare,
        compare_machines: dlMachines.map(function(m) { return m.title; }),
        compare_brands: dlMachines.map(function(m) { return m.brand; }),
        compare_types: dlMachines.map(function(m) { return (m.types || []).join(', '); }),
        compare_prices: dlMachines.map(function(m) { return m.isQuoteRequest ? 'call' : m.price; })
      });
      console.log('[Compare] dataLayer push:', state.compare);
      
      // Reset intent signals
      _mcaHasInteracted = false;
      if (_mcaCompareTimer) clearTimeout(_mcaCompareTimer);

      // Start "High Intent" timer (15 seconds)
      _mcaCompareTimer = setTimeout(() => {
        const machines = state.compare.map(h => state.machines.find(m => m.handle === h)).filter(Boolean);
        // Only track if they stayed 15s AND clicked something or expanded a spec
        if (_mcaHasInteracted) {
          trackKlaviyoCompare(machines);
        }
      }, 15000);

    } catch (err) { console.error('Compare view error:', err); dom.browseView.style.display = ''; dom.compareView.classList.remove('active'); }
  }

  function showBrowseView() {
    state.view = 'browse'; dom.browseView.style.display = ''; dom.compareView.classList.remove('active');
    history.replaceState(null, '', window.location.pathname);
  }

  function renderCompareTable() {
    const machines = state.compare.map(h => state.machines.find(m => m.handle === h)).filter(Boolean);
    if (!machines.length) return;
    const allCategories = new Set(); machines.forEach(m => { if (m.specs) Object.keys(m.specs).forEach(k => allCategories.add(k)); });
    const orderedCats = SPEC_CATEGORY_ORDER.filter(c => allCategories.has(c));
    allCategories.forEach(c => { if (!orderedCats.includes(c)) orderedCats.push(c); });
    const colCount = machines.length + 1;
    let html = '<thead><tr><th class="mca-compare-label-col"></th>';
    machines.forEach(m => {
      html += `<th class="mca-compare-product-header"><span class="mca-cph-remove" data-handle="${m.handle}" title="Remove">&times;</span>
        <div class="mca-cph-layout">${m.image ? `<a href="${m.url}" target="_blank" rel="noopener"><img src="${m.image}" alt="${m.brand}"></a>` : ''}
          <div class="mca-cph-info"><a href="${m.url}" class="mca-cph-title" target="_blank" rel="noopener">${stripBrand(m.title, m.brand)}</a></div>
        </div></th>`;
    });
    html += '</tr></thead><tbody>';
    html += '<tr class="mca-compare-spec-row mca-brand-row"><td class="mca-compare-label-col mca-ks-label">Brand</td>';
    machines.forEach(m => { html += `<td><div class="mca-cph-brand">${brandLink(m.brand)}</div></td>`; });
    html += '</tr><tr class="mca-compare-spec-row mca-price-row"><td class="mca-compare-label-col mca-ks-label">Price</td>';
    machines.forEach(m => {
      let ph = m.isQuoteRequest ? '<div class="mca-price-quote">Call for Price</div>' : (m.isOnSale && m.compareAtPrice ? `<div class="mca-price-container"><span class="mca-cph-compare-price">${formatPrice(m.compareAtPrice)}</span><span class="mca-cph-price">${formatPrice(m.price)}</span></div>` : `<div class="mca-cph-price">${formatPrice(m.price)}</div>`);
      html += `<td>${ph}</td>`;
    });
    html += '</tr><tr class="mca-compare-spec-row"><td class="mca-compare-label-col">Machine Type</td>';
    machines.forEach(m => { html += `<td>${m.types.map(t => `<span class="mca-type-badge">${t}</span>`).join(' ')}</td>`; });
    html += '</tr>';
    if (machines.some(m => m.weightLbs)) {
      html += '<tr class="mca-compare-spec-row"><td class="mca-compare-label-col">Weight</td>';
      machines.forEach(m => { html += `<td>${m.weightLbs ? m.weightLbs + ' lbs' : '-'}</td>`; });
      html += '</tr>';
    }
    orderedCats.forEach(cat => {
      if (!machines.some(m => m.specs && m.specs[cat] && m.specs[cat].length > 0)) return;
      html += `<tr class="mca-compare-section-row" data-section="${cat}"><td class="mca-compare-label-col" colspan="${colCount}">${cat}<span class="mca-section-toggle">&#9660;</span></td></tr>`;
      if (cat === 'Key Specs') {
        var ps = machines.map(m => extractKeySpecs(m)); var rows = getKeySpecRows(machines);
        rows.forEach(row => {
          if (!ps.some(p => p[row.key] != null)) return;
          html += '<tr class="mca-compare-spec-row mca-section-Key-Specs"><td class="mca-compare-label-col mca-ks-label">' + row.label + '</td>';
          ps.forEach(p => {
            let val = p[row.key];
            if (val && row.key === 'warranty') {
              // Strip redundant "Warranty:" or "Limited Warranty:" for display
              val = val.replace(/^(?:Limited\s+|Manufacturer's\s+)?Warranty:\s*/i, '');
              val = val.replace(/^Limited Warranty\s*/i, '');
            }
            html += val ? '<td>' + val + '</td>' : '<td class="mca-ks-na">N/A</td>';
          });
          html += '</tr>';
        });
      } else {
        var sc = cat.replace(/[^a-zA-Z0-9]+/g, '-').replace(/-+$/, '');
        html += `<tr class="mca-compare-spec-row mca-section-${sc}"><td class="mca-compare-label-col">Details</td>`;
        machines.forEach(m => { var i = (m.specs && m.specs[cat]) || []; html += i.length ? `<td><ul>${i.map(li => `<li>${li}</li>`).join('')}</ul></td>` : '<td>-</td>'; });
        html += '</tr>';
      }
    });
    html += '</tbody>'; dom.compareTable.innerHTML = html; dom.compareTable.setAttribute('data-count', machines.length);

    /* Add/update compare footer */
    var footerId = 'mca-compare-footer';
    var existingFooter = document.getElementById(footerId);
    if (existingFooter) existingFooter.remove();
    var footer = document.createElement('div');
    footer.id = footerId;
    footer.style.cssText = 'text-align:center;padding:24px 0 8px;';
    var compareUrl = window.location.href;
    footer.innerHTML = '<div style="display:flex;justify-content:center;flex-wrap:wrap;gap:8px;margin-bottom:16px;font-size:13px;font-weight:600;">'
      + '<a href="#" id="mca-print-btn" style="color:#203f9a;text-decoration:none;margin:0 12px;">Print Comparison</a>'
      + '<a href="#" id="mca-copy-link" style="color:#203f9a;text-decoration:none;margin:0 12px;">Copy Link</a>'
      + '<a href="https://www.qualitysewing.com/pages/sewing-machine-quiz" style="color:#203f9a;text-decoration:none;margin:0 12px;">Take the Quiz</a>'
      + '</div>'
      + '<div style="text-align:center;padding:14px 20px;background:linear-gradient(135deg,#f0f4ff 0%,#f8f0ff 100%);border-radius:10px;border:1px solid rgba(32,63,154,0.08);max-width:600px;margin:0 auto;">'
      + '<div style="font-size:14px;font-weight:700;color:#203f9a;margin-bottom:4px;">Need help? We are here for you.</div>'
      + '<div style="font-size:13px;color:#444;">'
      + '<a href="tel:18006875175" style="color:#E8008A;font-weight:700;text-decoration:none;">Call (800) 687-5175</a>'
      + '<span style="margin:0 8px;color:#ccc;">|</span>'
      + '<a href="https://www.qualitysewing.com/pages/store-locator" target="_blank" style="color:#203f9a;font-weight:600;text-decoration:none;">Visit one of our 11 locations</a>'
      + '</div></div>';
    dom.compareTable.parentElement.appendChild(footer);

    /* Bind print — opens comparison in a new window for clean printing */
    var printBtn = document.getElementById('mca-print-btn');
    if (printBtn) {
      printBtn.addEventListener('click', function(e) {
        e.preventDefault();
        var tableHtml = dom.compareTable.outerHTML;
        var printWin = window.open('', '_blank', 'width=900,height=700');
        printWin.document.write('<!DOCTYPE html><html><head><title>Machine Comparison</title>');
        printWin.document.write('<style>');
        printWin.document.write('body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;margin:20px;color:#333;}');
        printWin.document.write('.print-header{text-align:center;margin-bottom:16px;}');
        printWin.document.write('.print-header h2{font-size:22px;font-weight:800;color:#203f9a;margin:0 0 4px;}');
        printWin.document.write('.print-header p{font-size:14px;font-weight:700;color:#203f9a;margin:0;}');
        printWin.document.write('table{width:100%;border-collapse:collapse;font-size:12px;}');
        printWin.document.write('th,td{padding:6px 8px;border:1px solid #ddd;vertical-align:top;text-align:left;}');
        printWin.document.write('th{background:#203f9a;color:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;}');
        printWin.document.write('img{max-height:90px;max-width:100%;display:block;margin:0 auto;background:#fff;}');
        printWin.document.write('.mca-cph-layout{text-align:center;}');
        printWin.document.write('.mca-compare-product-header{background:#fff;text-align:center;}');
        printWin.document.write('ul{padding-left:16px;margin:0;}');
        printWin.document.write('li{font-size:11px;line-height:1.4;}');
        printWin.document.write('.mca-type-badge{display:inline-block;background:#f0f4ff;color:#203f9a;padding:2px 6px;border-radius:3px;font-size:10px;font-weight:600;margin:1px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}');
        printWin.document.write('.mca-cph-price{color:#E8008A;font-weight:800;font-size:14px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}');
        printWin.document.write('.mca-cph-compare-price{color:#999;text-decoration:line-through;font-size:12px;margin-right:6px;}');
        printWin.document.write('.mca-price-quote{color:#E8008A;font-weight:700;font-size:13px;}');
        printWin.document.write('.mca-cph-brand a{color:#203f9a;font-weight:700;text-decoration:none;font-size:12px;text-transform:uppercase;}');
        printWin.document.write('.mca-cph-title{color:#000;font-weight:700;font-size:13px;text-decoration:none;}');
        printWin.document.write('.mca-compare-section-row td{background:#f0f4ff;font-weight:700;color:#203f9a;font-size:13px;-webkit-print-color-adjust:exact;print-color-adjust:exact;}');
        printWin.document.write('.mca-section-toggle{display:none;}');
        printWin.document.write('.mca-cph-remove{display:none;}');
        printWin.document.write('.mca-ks-na{color:#ccc;}');
        printWin.document.write('@media print{@page{margin:0.4in;}}');
        printWin.document.write('</style></head><body>');
        printWin.document.write('<div class="print-header"><h2>Machine Comparison</h2><p>QualitySewing.com | (800) 687-5175</p></div>');
        printWin.document.write('<table>' + tableHtml.replace(/<\/?tbody>/gi, '').replace(/<\/?thead>/gi, '') + '</table>');
        printWin.document.write('</body></html>');
        printWin.document.close();
        printWin.focus();
        setTimeout(function() { printWin.print(); }, 500);
      });
    }

    /* Bind copy link */
    var copyBtn = document.getElementById('mca-copy-link');
    if (copyBtn) {
      copyBtn.addEventListener('click', function(e) {
        e.preventDefault();
        var url = window.location.origin + window.location.pathname + '?compare=' + state.compare.join(',');
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function() { copyBtn.textContent = 'Copied!'; setTimeout(function() { copyBtn.textContent = 'Copy Link'; }, 2000); });
        } else {
          var ta = document.createElement('textarea'); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
          copyBtn.textContent = 'Copied!'; setTimeout(function() { copyBtn.textContent = 'Copy Link'; }, 2000);
        }
      });
    }
  }

  function updatePriceSlider() {
    const min = parseInt(dom.priceMin.value), max = parseInt(dom.priceMax.value), range = parseInt(dom.priceMax.max) - parseInt(dom.priceMin.min);
    dom.rangeFill.style.left = ((min - parseInt(dom.priceMin.min)) / range) * 100 + '%';
    dom.rangeFill.style.width = ((max - min) / range) * 100 + '%';
    dom.priceMinLabel.textContent = formatPrice(min); dom.priceMaxLabel.textContent = formatPrice(max);
  }

  function bindEvents() {
    // Sidebar collapsible toggles
    $$('.mca-collapsible .mca-filter-header').forEach(header => {
      header.addEventListener('click', function() {
        header.parentElement.classList.toggle('expanded');
      });
    });

    dom.searchInput.addEventListener('input', debounce(function() { state.filters.search = this.value.trim(); applyFilters(); }, 300));
    dom.sortSelect.addEventListener('change', function() { state.sort = this.value; if (dom.sortSpecs) dom.sortSpecs.value = ''; applyFilters(); });
    if (dom.sortSpecs) dom.sortSpecs.addEventListener('change', function() { if (this.value) { state.sort = this.value; dom.sortSelect.value = 'price-asc'; } applyFilters(); });
    dom.typeFilters.addEventListener('change', function(e) { if (e.target.type !== 'checkbox') return; state.filters.types = Array.from(dom.typeFilters.querySelectorAll('input:checked')).map(i => i.value); if (dom.sortSpecs && dom.sortSpecs.value) { dom.sortSpecs.value = ''; state.sort = 'price-asc'; dom.sortSelect.value = 'price-asc'; } applyFilters(); });
    dom.brandFilters.addEventListener('change', function(e) { if (e.target.type !== 'checkbox') return; state.filters.brands = Array.from(dom.brandFilters.querySelectorAll('input:checked')).map(i => i.value); applyFilters(); });
    dom.brandToggle.addEventListener('click', function() { const ex = dom.brandFilters.querySelectorAll('.mca-brand-extra'), showing = ex[0] && ex[0].style.display !== 'none'; ex.forEach(el => el.style.display = showing ? 'none' : ''); this.textContent = showing ? `Show ${ex.length} more` : 'Show fewer'; });
    const ph = debounce(() => { let mi = parseInt(dom.priceMin.value), ma = parseInt(dom.priceMax.value); if (mi > ma) { dom.priceMin.value = ma; dom.priceMax.value = mi; mi = ma; ma = parseInt(dom.priceMax.value); } state.filters.priceMin = mi; state.filters.priceMax = ma; updatePriceSlider(); applyFilters(); }, 150);
    dom.priceMin.addEventListener('input', () => { updatePriceSlider(); ph(); }); dom.priceMax.addEventListener('input', () => { updatePriceSlider(); ph(); });
    dom.saleToggle.addEventListener('change', function() { state.filters.onSale = this.checked; applyFilters(); });
    $('#mca-special-filters').addEventListener('change', e => { if (e.target.type !== 'checkbox') return; const v = e.target.value; if (v === 'promo') state.filters.promo = e.target.checked; if (v === 'preowned') state.filters.preowned = e.target.checked; applyFilters(); });
    $('#mca-clear-all').addEventListener('click', () => { state.filters = { types: [], brands: [], priceMin: parseInt(dom.priceMin.min), priceMax: parseInt(dom.priceMax.max), onSale: false, search: '', promo: false, preowned: false }; dom.typeFilters.querySelectorAll('input').forEach(i => i.checked = false); dom.brandFilters.querySelectorAll('input').forEach(i => i.checked = false); dom.priceMin.value = dom.priceMin.min; dom.priceMax.value = dom.priceMax.max; updatePriceSlider(); dom.saleToggle.checked = false; $('#mca-special-filters').querySelectorAll('input').forEach(i => i.checked = false); dom.searchInput.value = ''; applyFilters(); });
    dom.activeFilters.addEventListener('click', e => { const c = e.target.closest('.mca-chip'); if (!c || !e.target.classList.contains('mca-chip-remove')) return; const t = c.dataset.type, v = c.dataset.value; if (t === 'type') { state.filters.types = state.filters.types.filter(x => x !== v); const cb = dom.typeFilters.querySelector(`input[value="${v}"]`); if (cb) cb.checked = false; } else if (t === 'brand') { state.filters.brands = state.filters.brands.filter(x => x !== v); const cb = dom.brandFilters.querySelector(`input[value="${v}"]`); if (cb) cb.checked = false; } else if (t === 'sale') { state.filters.onSale = false; dom.saleToggle.checked = false; } else if (t === 'promo') { state.filters.promo = false; $('#mca-special-filters').querySelector('input[value="promo"]').checked = false; } else if (t === 'preowned') { state.filters.preowned = false; $('#mca-special-filters').querySelector('input[value="preowned"]').checked = false; } else if (t === 'search') { state.filters.search = ''; dom.searchInput.value = ''; } else if (t === 'price') { state.filters.priceMin = parseInt(dom.priceMin.min); state.filters.priceMax = parseInt(dom.priceMax.max); dom.priceMin.value = dom.priceMin.min; dom.priceMax.value = dom.priceMax.max; updatePriceSlider(); } applyFilters(); });
    dom.grid.addEventListener('click', e => { const b = e.target.closest('.mca-btn-compare'); if (b) toggleCompare(b.dataset.handle); });
    dom.loadMore.addEventListener('click', () => { state.page++; renderCards(); });
    $('#mca-back-browse').addEventListener('click', showBrowseView);
    dom.compareTable.addEventListener('click', e => {
      _mcaHasInteracted = true;
      const r = e.target.closest('.mca-cph-remove'); if (r) { toggleCompare(r.dataset.handle); if (state.compare.length === 0) showBrowseView(); else renderCompareTable(); return; }
      const s = e.target.closest('.mca-compare-section-row'); if (!s) return; const cat = s.dataset.section, col = s.classList.toggle('collapsed'), sc = cat.replace(/[^a-zA-Z0-9]+/g, '-').replace(/-+$/, '');
      dom.compareTable.querySelectorAll(`.mca-section-${sc}`).forEach(row => row.style.display = col ? 'none' : '');
    });
    window.addEventListener('qsv-compare-updated', e => { if (_mcaBroadcasting) return; var h = (e.detail && e.detail.handles) || loadCompareFromStorage(); state.compare = h.filter(x => state.machines.some(m => m.handle === x)).slice(0, 3); updateCompareTray(); updateCardButtons(); if (state.view === 'compare' && state.compare.length === 0) showBrowseView(); else if (state.view === 'compare') renderCompareTable(); });
    // qsv-compare-now is registered early (before data fetch) to avoid race condition

    // Mobile filter drawer open/close
    var openFiltersBtn = $('#mca-open-filters');
    if (openFiltersBtn) {
      openFiltersBtn.addEventListener('click', function() {
        dom.sidebar.classList.add('open');
        dom.overlay.classList.add('active');
      });
    }
    if (dom.overlay) {
      dom.overlay.addEventListener('click', function() {
        dom.sidebar.classList.remove('open');
        dom.overlay.classList.remove('active');
      });
    }
    var closeFiltersBtn = $('#mca-close-filters');
    if (closeFiltersBtn) {
      closeFiltersBtn.addEventListener('click', function() {
        dom.sidebar.classList.remove('open');
        dom.overlay.classList.remove('active');
      });
    }

    initFloatingTray();
    updatePriceSlider();
  }

  const dataUrl = document.getElementById('machine-compare-app')?.dataset?.machinesUrl || 'data/machines.json';
  if (dom.grid) {
    var sk = ''; for (var i = 0; i < 12; i++) sk += '<article class="mca-card mca-card-loading"><div class="mca-card-image"><div class="mca-skeleton" style="width:100%;height:100%;position:absolute;top:0;left:0;"></div></div><div class="mca-card-body"><div class="mca-skeleton" style="height:18px;width:80%;margin-bottom:8px;"></div><div class="mca-skeleton" style="height:14px;width:50%;margin-bottom:12px;"></div><div class="mca-skeleton" style="height:32px;width:100%;"></div></div></article>';
    dom.grid.innerHTML = sk;
  }
  var livePriceCache = {}, priceAbortController = null;
  function fetchLivePrices(machines, batchSize) {
    if (priceAbortController) priceAbortController.abort(); priceAbortController = new AbortController(); var signal = priceAbortController.signal; batchSize = batchSize || 6;
    var queue = machines.filter(m => !livePriceCache[m.handle]), updated = 0;
    function processBatch() {
      if (signal.aborted) return; var batch = queue.splice(0, batchSize); if (!batch.length) { if (updated > 0) applyFilters(); return; }
      Promise.all(batch.map(m => fetch(m.url + '.json', { signal }).then(r => r.json()).then(d => { livePriceCache[m.handle] = true; if (!d.product) return; var p = d.product; if (!m.image && p.image && p.image.src) { m.image = p.image.src; updated++; } var v = p.variants && p.variants[0]; if (v) { var lp = parseFloat(v.price), lc = v.compare_at_price ? parseFloat(v.compare_at_price) : null; if (!isNaN(lp) && lp > 0 && (lp !== m.price || lc !== m.compareAtPrice)) { m.price = lp; m.compareAtPrice = lc; m.isOnSale = lc && lc > lp; updated++; } } }).catch(() => livePriceCache[m.handle] = true))).then(() => setTimeout(processBatch, 150));
    }
    processBatch();
  }

  function checkUrlParams() {
    var params = new URLSearchParams(window.location.search);

    /* Pre-filter by type (e.g., ?type=Serger or ?type=Embroidery) */
    var typeParam = params.get('type');
    if (typeParam) {
      var typeMap = { sewing: 'Sewing', quilting: 'Quilting', embroidery: 'Embroidery', serger: 'Serger', serging: 'Serger', longarm: 'Longarm' };
      var mappedType = typeMap[typeParam.toLowerCase()] || typeParam;
      var typeCheckboxes = dom.typeFilters.querySelectorAll('input[type="checkbox"]');
      typeCheckboxes.forEach(function(cb) {
        cb.checked = (cb.value === mappedType);
      });
      state.filters.types = [mappedType];
      applyFilters();
    }

    /* Pre-filter by brand (e.g., ?brand=Baby+Lock or ?brand=Bernina) */
    var brandParam = params.get('brand');
    if (brandParam) {
      var brands = brandParam.split(',').map(function(b) { return b.trim(); });
      var brandCheckboxes = dom.brandFilters.querySelectorAll('input[type="checkbox"]');
      brandCheckboxes.forEach(function(cb) {
        cb.checked = brands.some(function(b) { return cb.value.toLowerCase() === b.toLowerCase(); });
      });
      state.filters.brands = brands;
      applyFilters();
    }

    /* Pre-load specific machines to compare */
    var handles = [], compareParam = params.get('compare');
    if (compareParam) { handles = compareParam.split(',').filter(Boolean); }
    if (!handles.length) handles = loadCompareFromStorage();
    var valid = handles.filter(h => state.machines.some(m => m.handle === h)).slice(0, 3);
    if (!valid.length) return;
    state.compare = valid; updateCompareTray(); updateCardButtons();
    if (compareParam) showCompareView();
  }

  fetch(dataUrl).then(r => r.json()).then(data => { init(data); checkUrlParams(); setTimeout(() => { fetchLivePrices(state.filtered); setTimeout(() => { var rem = state.machines.filter(m => !livePriceCache[m.handle]); if (rem.length) fetchLivePrices(rem); }, 3000); }, 1000); }).catch(err => { console.error('Data load fail:', err); dom.grid.innerHTML = '<p style=\"padding:40px;text-align:center;color:#999\">Failed to load machine data.</p>'; });
})();
